// @ts-check
/** @typedef {import('../types/api').EmailTemplate} EmailTemplate */

import DOMPurify from 'dompurify';
import { AlertCircle, Check, Code, Edit, Eye, Mail, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import Modal from '../components/ui/Modal';
import PreviewModal from '../components/ui/PreviewModal';
import adminEmailTemplateService from '../services/adminEmailTemplateService';

const EmailTemplatesPage = () => {
  /** @type {[EmailTemplate[], Function]} */
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorModal, setEditorModal] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  // 'saved' = read-only preview of an already-saved template (from the card's Preview
  // button); 'draft' = preview of unsaved editor content, gated in front of Save.
  const [previewSource, setPreviewSource] = useState('saved');
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormDataRaw] = useState({
    name: '',
    subject: '',
    html_body: '',
    text_body: '',
    template_type: 'payment_reminder',
    from_email: 'orders@kaay.co',
  });
  const setFormData = useCallback((updater) => {
    setIsDirty(true);
    setFormDataRaw(updater);
  }, []);

  // X and Cancel dismiss the editor modal — confirm first if there's
  // anything to lose. A successful save resets editorModal directly inside
  // handleSave, bypassing this.
  const closeEditor = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setEditorModal(null);
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await adminEmailTemplateService.getAllTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setFormDataRaw({
      name: '',
      subject: '',
      html_body: getDefaultTemplate('payment_reminder'),
      text_body: '',
      template_type: 'payment_reminder',
      from_email: 'orders@kaay.co',
    });
    setIsDirty(false);
    setEditorModal('create');
  };

  const openEditModal = async (template) => {
    setFormDataRaw({
      name: template.name,
      subject: template.subject,
      html_body: template.html_body,
      text_body: template.text_body || '',
      template_type: template.template_type,
      from_email: template.from_email,
    });
    setIsDirty(false);
    setEditorModal(template.id);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editorModal === 'create') {
        await adminEmailTemplateService.createTemplate(formData);
      } else {
        await adminEmailTemplateService.updateTemplate(editorModal, formData);
      }
      setIsDirty(false);
      setEditorModal(null);
      setPreviewModal(null);
      setPreviewData(null);
      loadTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template: ' + (error.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await adminEmailTemplateService.deleteTemplate(templateId);
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Failed to delete template');
    }
  };

  const handlePreview = async (template) => {
    const sampleVars = getSampleVariables(template.template_type);

    try {
      const preview = await adminEmailTemplateService.previewTemplate(template.id, sampleVars);
      setPreviewData(preview);
      setPreviewSource('saved');
      setPreviewModal(template);
    } catch (error) {
      console.error('Failed to preview template:', error);
      alert('Failed to preview template');
    }
  };

  // Preview the editor's current (unsaved) content — used by the editor's own Preview
  // button and by "Save Template", which routes through this before ever writing to the DB.
  const handlePreviewDraft = async () => {
    const sampleVars = getSampleVariables(formData.template_type);

    try {
      const preview = await adminEmailTemplateService.previewDraft({
        subject: formData.subject,
        html_body: formData.html_body,
        text_body: formData.text_body,
        variables: sampleVars,
      });
      setPreviewData(preview);
      setPreviewSource('draft');
      setPreviewModal(formData);
    } catch (error) {
      console.error('Failed to preview draft template:', error);
      alert('Failed to preview template');
    }
  };

  const getSampleVariables = (type) => {
    const samples = {
      payment_reminder: {
        customer_name: 'Jane Doe',
        order_number: 'ORD-12345',
        order_total: '₹15,000',
        minutes_remaining: '20',
        payment_link: 'https://kaay.co/order-confirmation/abc123?pay=true',
        track_link: 'https://kaay.co/track-order',
      },
      order_expired: {
        customer_name: 'Jane Doe',
        order_number: 'ORD-12345',
        order_total: '₹15,000',
        shop_link: 'https://kaay.co/ready-to-wear',
      },
      order_confirmation: {
        customer_name: 'Jane Doe',
        order_id: 'ORD-12345',
        order_total: '₹15,000',
        order_date: 'May 9, 2026',
        shipping_address: '42 Marine Drive, Mumbai, 400001',
        order_items_html: '<p style="color:#999">[ Order items table ]</p>',
        support_email: 'concierge@kaay.co',
      },
      wholesale: {
        name: 'Richa',
        store: 'Bombaim',
        custom_paragraph: 'Your edit is the company we want to keep.',
        city: 'Mumbai',
        date: '26 Oct',
        phone: '+91 00000 00000',
        subject: 'A six-style edit for Bombaim',
      },
      shipping_notification: {
        customer_name: 'Jane Doe',
        order_id: 'ORD-12345',
        tracking_number: 'FX987654321IN',
        carrier: 'FedEx',
        tracking_url: 'https://www.fedex.com/fedextrack/?trknbr=FX987654321IN',
        estimated_delivery: 'May 13, 2026',
        support_email: 'concierge@kaay.co',
      },
    };
    return samples[type] || {};
  };

  const getDefaultTemplate = (type) => {
    const kaayHeader = `<tr><td style="background:#000000;padding:24px 40px;text-align:center;"><span style="font-size:20px;letter-spacing:0.3em;color:#ffffff;font-family:'Faber Serif Pro',Georgia,'Times New Roman',serif;">KAAY</span></td></tr>`;
    const kaayFooter = `<tr><td style="border-top:1px solid #eeeeee;padding:20px 40px;text-align:center;"><p style="margin:0;font-size:11px;color:#bbbbbb;letter-spacing:0.05em;font-family:'Faber Serif Pro',Georgia,'Times New Roman',serif;">© KAAY · kaay.co</p></td></tr>`;
    const wrap = (rows) =>
      `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background:#f5f5f5;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f5f5f5;padding:40px 16px;"><tr><td align="center"><table width="100%" cellpadding="0" cellspacing="0" style="max-width:560px;background:#ffffff;">${kaayHeader}${rows}${kaayFooter}</table></td></tr></table></body></html>`;

    const templates = {
      payment_reminder: wrap(`<tr><td style="padding:40px 40px 32px;">
  <p style="margin:0 0 4px;font-size:10px;letter-spacing:0.15em;color:#999999;text-transform:uppercase;">Payment Required</p>
  <p style="margin:0 0 24px;font-size:15px;line-height:1.6;color:#111111;font-family:Georgia,serif;">Dear {{customer_name}},</p>
  <div style="font-size:14px;line-height:1.75;color:#444444;">
    <p>Your order <strong>#{{order_number}}</strong> is reserved and awaiting payment.</p>
    <p>You have <strong>{{minutes_remaining}} minutes</strong> remaining to complete your purchase — after which the items will be released.</p>
    <p style="margin-top:28px;"><a href="{{payment_link}}" style="display:inline-block;background:#000000;color:#ffffff;padding:12px 32px;text-decoration:none;font-size:11px;letter-spacing:0.12em;">COMPLETE PAYMENT</a></p>
    <p style="margin-top:24px;font-size:13px;color:#999999;">Order total: {{order_total}}</p>
  </div>
  <p style="margin:36px 0 0;font-size:13px;color:#999999;">— KAAY</p>
</td></tr>`),

      order_expired: wrap(`<tr><td style="padding:40px 40px 32px;">
  <p style="margin:0 0 4px;font-size:10px;letter-spacing:0.15em;color:#999999;text-transform:uppercase;">Reservation Lapsed</p>
  <p style="margin:0 0 24px;font-size:15px;line-height:1.6;color:#111111;font-family:Georgia,serif;">Dear {{customer_name}},</p>
  <div style="font-size:14px;line-height:1.75;color:#444444;">
    <p>Your reservation for order <strong>#{{order_number}}</strong> has lapsed. The payment window has closed and the items have been returned to our collection.</p>
    <p>If you'd still like to purchase these pieces, please place a new order.</p>
    <p style="margin-top:28px;"><a href="{{shop_link}}" style="display:inline-block;background:#000000;color:#ffffff;padding:12px 32px;text-decoration:none;font-size:11px;letter-spacing:0.12em;">BROWSE COLLECTION</a></p>
  </div>
  <p style="margin:36px 0 0;font-size:13px;color:#999999;">— KAAY</p>
</td></tr>`),

      order_confirmation: wrap(`<tr><td style="padding:40px 40px 24px;">
  <p style="margin:0 0 4px;font-size:10px;letter-spacing:0.15em;color:#999999;text-transform:uppercase;">Order Confirmed</p>
  <p style="margin:0 0 24px;font-size:15px;line-height:1.6;color:#111111;font-family:Georgia,serif;">Dear {{customer_name}},</p>
  <p style="margin:0 0 28px;font-size:14px;line-height:1.75;color:#444444;">Thank you for your order. We're preparing it with care and will write to you as soon as it's dispatched.</p>
  <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #eeeeee;margin-bottom:28px;">
    <tr>
      <td style="padding:12px 16px;border-right:1px solid #eeeeee;">
        <p style="margin:0 0 2px;font-size:10px;letter-spacing:0.1em;color:#999999;text-transform:uppercase;">Order</p>
        <p style="margin:0;font-size:13px;color:#111111;">#{{order_id}}</p>
      </td>
      <td style="padding:12px 16px;">
        <p style="margin:0 0 2px;font-size:10px;letter-spacing:0.1em;color:#999999;text-transform:uppercase;">Date</p>
        <p style="margin:0;font-size:13px;color:#111111;">{{order_date}}</p>
      </td>
    </tr>
  </table>
  {{order_items_html}}
  <p style="margin:16px 0 0;font-size:13px;color:#999999;">Shipping to: {{shipping_address}}</p>
  <p style="margin:32px 0 0;font-size:13px;color:#999999;">— KAAY</p>
</td></tr>`),

      shipping_notification: wrap(`<tr><td style="padding:40px 40px 32px;">
  <p style="margin:0 0 4px;font-size:10px;letter-spacing:0.15em;color:#999999;text-transform:uppercase;">On Its Way</p>
  <p style="margin:0 0 24px;font-size:15px;line-height:1.6;color:#111111;font-family:Georgia,serif;">Dear {{customer_name}},</p>
  <p style="margin:0 0 28px;font-size:14px;line-height:1.75;color:#444444;">Your order <strong>#{{order_id}}</strong> has been dispatched and is on its way to you.</p>
  <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #eeeeee;margin-bottom:28px;">
    <tr><td colspan="2" style="padding:10px 16px;border-bottom:1px solid #eeeeee;background:#fafafa;">
      <span style="font-size:10px;letter-spacing:0.15em;color:#999999;text-transform:uppercase;">Shipping Details</span>
    </td></tr>
    <tr>
      <td style="padding:12px 16px;border-bottom:1px solid #eeeeee;"><span style="font-size:11px;letter-spacing:0.1em;color:#999999;text-transform:uppercase;">Carrier</span></td>
      <td style="padding:12px 16px;border-bottom:1px solid #eeeeee;text-align:right;"><span style="font-size:13px;color:#111111;">{{carrier}}</span></td>
    </tr>
    <tr>
      <td style="padding:12px 16px;border-bottom:1px solid #eeeeee;"><span style="font-size:11px;letter-spacing:0.1em;color:#999999;text-transform:uppercase;">Tracking</span></td>
      <td style="padding:12px 16px;border-bottom:1px solid #eeeeee;text-align:right;"><a href="{{tracking_url}}" style="font-size:13px;color:#000000;text-decoration:underline;">{{tracking_number}}</a></td>
    </tr>
    <tr>
      <td style="padding:12px 16px;"><span style="font-size:11px;letter-spacing:0.1em;color:#999999;text-transform:uppercase;">Est. Delivery</span></td>
      <td style="padding:12px 16px;text-align:right;"><span style="font-size:13px;color:#111111;">{{estimated_delivery}}</span></td>
    </tr>
  </table>
  <p style="margin:0 0 32px;"><a href="{{tracking_url}}" style="display:inline-block;background:#000000;color:#ffffff;padding:12px 32px;text-decoration:none;font-size:11px;letter-spacing:0.12em;">TRACK MY ORDER</a></p>
  <p style="margin:0;font-size:13px;color:#999999;">— KAAY</p>
</td></tr>`),
      // Wholesale outreach is a plain-text message the founder sends by hand (or pastes into a DM), not a branded email.
      wholesale: 'Hello {{name}},\n\n{{custom_paragraph}}\n\nSumit, Founder, KAAY',
    };
    return templates[type] || templates.payment_reminder;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-serif">Email Templates</h1>
          <p className="text-muted-foreground mt-1">Manage transactional email templates with Postmark</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center space-x-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90"
        >
          <Plus size={20} />
          <span>New Template</span>
        </button>
      </div>

      {/* Postmark Setup Notice */}
      <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 rounded-lg p-4 flex items-start space-x-3">
        <AlertCircle className="text-blue-600 dark:text-blue-400 mt-0.5" size={20} />
        <div>
          <h3 className="font-medium text-blue-900 dark:text-blue-300">Postmark Integration Required</h3>
          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
            To send emails, you need to set up Postmark. Check the deployment guide for setup instructions.
          </p>
        </div>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <Mail size={48} className="mx-auto text-muted-foreground/70 mb-4" />
          <h2 className="text-xl font-medium text-foreground mb-2">No Email Templates</h2>
          <p className="text-muted-foreground max-w-md mx-auto mb-4">
            Create your first email template to start sending transactional emails
          </p>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90"
          >
            <Plus size={18} />
            <span>Create Template</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-medium text-foreground">{template.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{template.template_type.replace('_', ' ')}</p>
                </div>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md ${
                    template.is_active
                      ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {template.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="text-sm text-muted-foreground mb-3">
                <p className="truncate">Subject: {template.subject}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Variables: {template.variables.length > 0 ? template.variables.join(', ') : 'None'}
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePreview(template)}
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-1.5 border border-input rounded-lg text-sm hover:bg-muted/50"
                >
                  <Eye size={14} />
                  <span>Preview</span>
                </button>
                <button
                  onClick={() => openEditModal(template)}
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-1.5 border border-input rounded-lg text-sm hover:bg-muted/50"
                >
                  <Edit size={14} />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => handleDelete(template.id)}
                  className="px-3 py-1.5 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg text-sm hover:bg-red-50 dark:hover:bg-red-950/70"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {editorModal && (
        <Modal
          maxWidth="5xl"
          onClose={closeEditor}
          closeOnBackdrop={false}
          className="rounded-lg shadow-xl flex flex-col"
          hasUnsavedChanges={isDirty}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h3 className="text-lg font-medium">
              {editorModal === 'create' ? 'Create Email Template' : 'Edit Email Template'}
            </h3>
            <button onClick={closeEditor} className="text-muted-foreground/70 hover:text-foreground">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">Template Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground"
                  placeholder="order_confirmation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">Template Type *</label>
                <select
                  value={formData.template_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      template_type: e.target.value,
                      html_body: getDefaultTemplate(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground"
                >
                  <option value="payment_reminder">Payment Reminder</option>
                  <option value="order_expired">Order Expired</option>
                  <option value="order_confirmation">Order Confirmation</option>
                  <option value="shipping_notification">Shipping Notification</option>
                  <option value="review_invite">Review Invite</option>
                  <option value="wholesale">Wholesale outreach</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">From Email *</label>
              <input
                type="email"
                value={formData.from_email}
                onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground"
                placeholder="noreply@kaay.co"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">
                Subject Line *{' '}
                <span className="text-muted-foreground text-xs">
                  (Use {'{{'} variable {'}}'} for dynamic content)
                </span>
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground"
                placeholder="Order Confirmation: {{order_number}}"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1 flex items-center space-x-2">
                <Code size={16} />
                <span>
                  HTML Body *{' '}
                  <span className="text-muted-foreground text-xs">
                    (Use {'{{'} variable {'}}'} syntax)
                  </span>
                </span>
              </label>
              <textarea
                value={formData.html_body}
                onChange={(e) => setFormData({ ...formData, html_body: e.target.value })}
                rows={15}
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground font-mono text-sm"
                placeholder="<html>...</html>"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">Plain Text Version (Optional)</label>
              <textarea
                value={formData.text_body}
                onChange={(e) => setFormData({ ...formData, text_body: e.target.value })}
                rows={5}
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground"
                placeholder="Plain text version..."
              />
            </div>
          </div>

          <div className="px-6 py-4 bg-muted/50 border-t border-border flex justify-end space-x-3">
            <button onClick={closeEditor} className="px-4 py-2 border border-input rounded-lg hover:bg-accent">
              Cancel
            </button>
            <button
              onClick={handlePreviewDraft}
              className="px-4 py-2 border border-input rounded-lg hover:bg-accent flex items-center space-x-2"
            >
              <Eye size={16} />
              <span>Preview</span>
            </button>
            <button
              onClick={handlePreviewDraft}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 flex items-center space-x-2"
            >
              <Check size={16} />
              <span>Save Template</span>
            </button>
          </div>
        </Modal>
      )}

      {/* Preview Modal — 'draft' (from the editor) offers Confirm & Save; 'saved'
          (from a template card) is read-only since there's nothing pending to save. */}
      {previewModal && previewData && (
        <PreviewModal
          title={`Preview: ${previewModal.name}`}
          subtitle={`Subject: ${previewData.subject}`}
          onClose={() => {
            setPreviewModal(null);
            setPreviewData(null);
          }}
          onConfirmSave={previewSource === 'draft' ? handleSave : undefined}
          saving={saving}
        >
          <div className="scheme-light bg-white text-gray-900 border border-border rounded-lg overflow-hidden">
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewData.html_body) }} />
          </div>
        </PreviewModal>
      )}
    </div>
  );
};

export default EmailTemplatesPage;
